/**
 * The Vibe app sign-in handshake's URLs: the console route the edge sends a
 * browser to, the callback on the app's own host, and the one rule both sides
 * apply to where a sign-in may return.
 *
 * Split from `edge-authz-endpoints.ts`, which keeps what the authorizer and
 * Traefik agree on; this file is what the console and the authorizer agree on.
 * The one-time code that now rides the callback is `vibe-session-code.schema.ts`.
 */

/**
 * Console route that performs the REAL authorization check — Clerk identity,
 * `PermissionCheckerService.canAccess()`, the org's actual grants — and mints a
 * short-lived app-scoped assertion. The authorizer redirects here and never
 * evaluates policy itself, which is what keeps the policy table and the user list
 * out of the tenant and the control plane off the request hot path.
 */
export const VIBE_AUTHORIZE_PATH = "/vibe/authorize";

/** Query parameter naming the app the caller was trying to reach. */
export const VIBE_AUTHORIZE_APP_PARAM = "app";

/** Query parameter carrying the absolute URL to return to after sign-in. */
export const VIBE_AUTHORIZE_RETURN_TO_PARAM = "return_to";

/**
 * Path on the APP's own host where a minted assertion is handed back.
 *
 * This exists because a cookie cannot cross an origin. The console mints the
 * assertion, but a `Set-Cookie` it writes lands on the console's domain and the
 * app's host would never see it. So the console sends the browser to this path
 * on the app's own host, where the ForwardAuth middleware — which already sees
 * every request to that host — recognises it, verifies the assertion, sets the
 * cookie, and redirects on. The request never reaches the app.
 *
 * Dot-prefixed and deliberately unlikely: it shadows this path on every Vibe
 * app, so it has to be a path no application would plausibly serve. Same
 * reasoning as Cloudflare Access's `/cdn-cgi/access/*`.
 */
export const VIBE_CALLBACK_PATH = "/.vibe/callback";

/**
 * LEGACY query parameter carrying the whole signed assertion to the callback.
 *
 * Replaced by the one-time code (`VIBE_CALLBACK_CODE_PARAM`, see
 * `vibe-session-code.schema.ts` for why): a URL reaches history and every access
 * log on the way, and a renewable assertion is a credential for hours. It
 * survives only so a router and a control plane on different sides of the
 * rollout still sign people in — the control plane emits it for a router that
 * did not ask for a code, and a new router still accepts it.
 *
 * debt: both ends still speak the assertion-in-URL callback for one release.
 *       Upgrade trigger: every tenant router has converged onto an edge bundle
 *       that sends `response_type=code` — measured as no
 *       "legacy assertion-in-URL callback" WARN from `MintVibeAppSessionUseCase`
 *       over a full week — then delete this constant, the mint's legacy branch
 *       and the edge's legacy reader together.
 */
export const VIBE_CALLBACK_ASSERTION_PARAM = "assertion";

/**
 * Query parameter naming where to go once the cookie is set.
 *
 * **Path-only, and the authorizer enforces that.** An absolute URL here would
 * make every Vibe app an open redirector on a host a user has just been told to
 * trust. A value that is not a single leading `/` is refused rather than
 * sanitised — there is no ambiguity worth accommodating.
 */
export const VIBE_CALLBACK_RETURN_TO_PARAM = "return_to";

/**
 * The three characters the WHATWG URL parser removes from its input before
 * parsing it, so a check over the literal string sees something the browser
 * never will.
 *
 * Written out rather than spelled `\s`, which additionally matches the space
 * and the vertical tab — neither of which the parser strips, so refusing them
 * would reject a path over a property it does not have.
 */
const URL_PARSER_STRIPPED = /[\t\n\r]/;

/**
 * A `return_to` that is safe to send a browser to on the app's own host, or
 * `null`.
 *
 * **Single-sourced because two programs that cannot see each other both decide
 * it, and they disagreed.** The console mints the callback URL and the edge
 * authorizer validates it, and a value the minter accepts but the verifier
 * refuses does not fail loudly — the callback is simply not RECOGNISED as one,
 * so it falls through to the ordinary access decision, bounces back to sign-in,
 * mints again, and the person ends up at `/` wondering why. A silent loop, from
 * two implementations of one rule.
 *
 * The rule: a single leading slash, and nothing that a browser would read as an
 * origin.
 *
 * - `//evil.example` is the case a naive "starts with a slash" test misses — a
 *   browser reads a protocol-relative URL as absolute, so it is an off-host
 *   redirect wearing a path's clothes. Rejected explicitly.
 * - A backslash is refused for the same reason: some clients normalise it to a
 *   forward slash AFTER a check like this would have passed.
 * - TAB, LF and CR are refused because the URL parser DELETES them before it
 *   parses, so they hide the case above rather than being a case of their own:
 *   `/<TAB>/evil.example` passes every test written over the literal string and
 *   is then read as `//evil.example`. Exactly these three and no other control
 *   character — a wider rule would refuse paths over a property they do not
 *   have, and the spec beside this file sweeps C0 to pin the boundary.
 *
 * **A caller that has already round-tripped the value through `new URL` cannot
 * produce one, which is why this was survivable rather than live.** The parser
 * strips the three on the way in, so a `pathname` never carries one and the
 * console's minting path was covered by a step it takes for another reason. A
 * caller reading the value out of `URLSearchParams` percent-DECODES instead, so
 * `return_to=%2F%09%2Fevil.example` arrives as a raw TAB — and a value then used
 * as a RELATIVE `Location` resolves off-host. One consumer does that: the edge
 * authorizer's callback response, on the branch where it knows no app host and
 * falls back to emitting the bare path.
 *
 * Re-derive rather than trusting the three, against a control that must stay on
 * the app's own host:
 *
 * ```
 * new URL("/\t/evil.example", "https://app.example").origin  // https://evil.example
 * new URL("/a/evil.example",  "https://app.example").origin  // https://app.example
 * ```
 *
 * Strict by design — it never substitutes a default. A caller that wants "empty
 * means the app root" says so itself, because the two callers want different
 * things: the edge defaults an absent value to `/`, while the console refuses a
 * malformed one outright rather than turning a deliberate probe into a
 * successful sign-in nobody sees in a log.
 */
export function vibeSafeReturnPath(candidate: string): string | null {
  if (!candidate.startsWith("/")) return null;
  if (candidate.startsWith("//") || candidate.startsWith("/\\")) return null;
  if (candidate.includes("\\")) return null;
  if (URL_PARSER_STRIPPED.test(candidate)) return null;

  return candidate;
}
